import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { api, ApiError } from "@/services/api.ts";
import type { SourcesFilters, VulnerabilityWatch } from "@/types";

/** Without a known product there is nothing to match against, so a count of 0 would be a guess. */
export function isWatchKnown(data: VulnerabilityWatch): boolean {
  return data.available && data.kev !== null && data.products.some((p) => p.product !== null);
}

export function useVulnerabilityWatch(enabled = true) {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["vulnerabilityWatch", effectiveCompanyId],
    queryFn: () => api.vulnerabilityWatch.get(),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!effectiveCompanyId,
  });
}

export function usePortalOverview() {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["portalOverview", effectiveCompanyId],
    queryFn: () => api.vulnerabilityWatch.getPortalOverview(),
    staleTime: 10 * 60 * 1000,
    enabled: !!effectiveCompanyId,
  });
}

export function usePortalSources(filters: SourcesFilters, page: number) {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["portalSources", effectiveCompanyId, filters, page],
    queryFn: () => api.vulnerabilityWatch.getPortalSources(filters, page),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: !!effectiveCompanyId,
  });
}

export function useVulnerability(cve: string) {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["vulnerability", effectiveCompanyId, cve],
    queryFn: () => api.vulnerabilityWatch.getOne(cve),
    staleTime: 5 * 60 * 1000,
    enabled: !!effectiveCompanyId,
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 404) && failureCount < 2,
  });
}
